'use client';
import { useState, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';

const TEAL      = '#1bb8a7';
const TEAL_LIGHT = '#f0fdfa';
const TEAL_MID   = '#99f6e4';
const STAR_COLOR = '#f59e0b';

/* ─── Mini helpers ─────────────────────────────────────────── */

const StarRow = ({ filled = 0, size = '0.7rem' }) => (
    <div style={{ display: 'flex', gap: '2px' }}>
        {[1,2,3,4,5].map(s => (
            <i key={s}
                className={`pi ${s <= filled ? 'pi-star-fill' : 'pi-star'}`}
                style={{ fontSize: size, color: s <= filled ? STAR_COLOR : '#cbd5e1' }}
            />
        ))}
    </div>
);

const DistBar = ({ star, count, total }) => {
    const pct   = total > 0 ? Math.round((count / total) * 100) : 0;
    const color = star >= 4 ? '#10b981' : star === 3 ? '#f59e0b' : '#ef4444';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', width: '30px', flexShrink: 0 }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>{star}</span>
                <i className="pi pi-star-fill" style={{ fontSize: '0.5rem', color: STAR_COLOR }} />
            </div>
            <div style={{ flex: 1, height: '7px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
                <div style={{
                    height: '100%', width: `${pct}%`,
                    background: color,
                    borderRadius: '99px',
                    transition: 'width 0.7s cubic-bezier(.4,0,.2,1)',
                }} />
            </div>
            <span style={{ fontSize: '0.68rem', color: '#94a3b8', width: '30px', textAlign: 'right', flexShrink: 0 }}>
                {pct}%
            </span>
        </div>
    );
};

const InsightBar = ({ label, value }) => {
    if (!value) return null;
    const pct = Math.round((value / 5) * 100);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '0.72rem', color: '#64748b', width: '56px', flexShrink: 0 }}>{label}</span>
            <div style={{ flex: 1, height: '6px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
                <div style={{
                    height: '100%', width: `${pct}%`,
                    background: 'var(--primary-color)',
                    borderRadius: '99px',
                }} />
            </div>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: TEAL, width: '28px', textAlign: 'right' }}>
                {value?.toFixed(1)}
            </span>
        </div>
    );
};

const ReviewCard = ({ review }) => (
    <div style={{
        padding: '14px',
        borderRadius: '12px',
        border: '1px solid #f1f5f9',
        background: '#f8fafc',
        marginBottom: '10px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {review.customerName && (
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>
                        {review.customerName}
                    </p>
                )}
                <StarRow filled={review.rating} />
                {review.title && (
                    <p style={{ margin: 0, fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                        {review.title}
                    </p>
                )}
            </div>
            {review.isVerifiedPurchase && (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                    background: '#dcfce7', color: '#16a34a',
                    border: '1px solid #bbf7d0', borderRadius: '20px',
                    padding: '2px 8px', fontSize: '0.6rem', fontWeight: 700,
                    flexShrink: 0, marginLeft: '8px',
                }}>
                    <i className="pi pi-verified" style={{ fontSize: '0.55rem' }} /> Verified
                </span>
            )}
        </div>
        {review.comment && (
            <p style={{ margin: 0, fontSize: '0.76rem', color: '#475569', lineHeight: '1.6' }}>
                {review.comment}
            </p>
        )}
    </div>
);

/* ─── Main export ───────────────────────────────────────────── */

export const RatingDetailsSidebar = ({ product, visible, onHide }) => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(false);
    const [page,    setPage]    = useState(1);

    const productId   = product?._id;
    const coverImage  = product?.coverImage;
    const productName = product?.title;

    useEffect(() => {
        if (!visible || !productId) return;
        setLoading(true);
        setData(null);
        setPage(1);
        fetch(`/api/review/${productId}?limit=10`)
            .then(r => r.json())
            .then(json => setData(json?.data || null))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [visible, productId]);

    const loadMore = () => {
        const next = page + 1;
        setPage(next);
        fetch(`/api/review/${productId}?page=${next}&limit=10`)
            .then(r => r.json())
            .then(json => {
                if (json?.data?.reviews) {
                    setData(prev => ({
                        ...json.data,
                        reviews: [...(prev?.reviews || []), ...json.data.reviews],
                    }));
                }
            })
            .catch(() => {});
    };

    const summary     = data?.summary;
    const reviews     = data?.reviews || [];
    const totalRevs   = summary?.totalReviews || reviews.length || 0;
    const reviewCount = totalRevs || product?.reviewCount || 0;
    const computedAvg = reviews.length
        ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length
        : 0;
    const avgRating   = summary?.avgRating || computedAvg || product?.averageRating || 0;

    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => { if (dist[r.rating] !== undefined) dist[r.rating]++; });

    const sidebarHeader = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
                width: '32px', height: '32px', borderRadius: '9px',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: 'none',
            }}>
                <i className="pi pi-star-fill" style={{ fontSize: '0.85rem', color: '#fff' }} />
            </div>
            <div>
                <div style={{ fontWeight: 800, fontSize: '1rem', color: 'white', letterSpacing: '-0.2px' }}>
                    Ratings & Reviews
                </div>
                {reviewCount > 0 && (
                    <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.7)', marginTop: '1px' }}>
                        {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Sidebar
            visible={visible}
            onHide={onHide}
            position="right"
            style={{ width: '400px', maxWidth: '95vw' }}
            header={sidebarHeader}
            className="p-0"
            pt={{ header: { style: { background: 'var(--primary-color)', borderBottom: 'none', padding: '12px 16px' } } }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                {/* ── Product hero ──────────────────────────── */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '14px 16px',
                    background: '#fff',
                    borderBottom: `2px solid ${TEAL_MID}`,
                }}>
                    {coverImage ? (
                        <div style={{
                            width: '56px', height: '56px', borderRadius: '12px',
                            background: '#fff', border: `2px solid ${TEAL_MID}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, overflow: 'hidden', padding: '4px',
                            boxShadow: `0 4px 12px ${TEAL}22`,
                        }}>
                            <img src={coverImage} alt={productName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                    ) : (
                        <div style={{
                            width: '56px', height: '56px', borderRadius: '12px',
                            background: `${TEAL}18`, border: `2px solid ${TEAL_MID}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <i className="pi pi-box" style={{ color: TEAL, fontSize: '1.4rem' }} />
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{
                            margin: '0 0 6px', fontWeight: 700, fontSize: '0.82rem', color: '#1e293b',
                            display: '-webkit-box', WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>{productName}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                background: `${TEAL}18`, color: TEAL,
                                border: `1px solid ${TEAL}40`, borderRadius: '20px',
                                padding: '3px 10px', fontSize: '0.7rem', fontWeight: 800,
                            }}>
                                <i className="pi pi-star-fill" style={{ fontSize: '0.55rem' }} />
                                {avgRating > 0 ? avgRating.toFixed(1) : '–'}
                            </span>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                                background: '#f1f5f9', color: '#64748b',
                                border: '1px solid #e2e8f0', borderRadius: '20px',
                                padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600,
                            }}>
                                {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Scrollable body ───────────────────────── */}
                <div style={{ overflowY: 'auto', flex: 1, padding: '16px' }}>

                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: '14px' }}>
                            <div style={{
                                width: '52px', height: '52px', borderRadius: '50%',
                                background: `${TEAL}18`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.6rem', color: TEAL }} />
                            </div>
                            <span style={{ fontSize: '0.82rem', color: '#94a3b8', fontWeight: 500 }}>Loading reviews…</span>
                        </div>

                    ) : reviewCount === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 0' }}>
                            <div style={{
                                width: '64px', height: '64px', borderRadius: '50%',
                                background: '#f1f5f9',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 16px',
                            }}>
                                <i className="pi pi-star" style={{ fontSize: '1.8rem', color: '#cbd5e1' }} />
                            </div>
                            <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: '0.9rem', color: '#475569' }}>No reviews yet</p>
                            <p style={{ margin: 0, fontSize: '0.76rem', color: '#94a3b8' }}>Be the first to review this product</p>
                        </div>

                    ) : (
                        <>
                            {/* ── Rating summary card ── */}
                            <div style={{
                                borderRadius: '16px',
                                border: `1px solid ${TEAL_MID}`,
                                background: '#fff',
                                padding: '18px',
                                marginBottom: '14px',
                                boxShadow: `0 4px 20px ${TEAL}14`,
                            }}>
                                <div style={{ display: 'flex', gap: '18px', alignItems: 'center', marginBottom: '16px' }}>
                                    {/* Big number */}
                                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                                        <div style={{
                                            fontSize: '3.2rem', fontWeight: 900, color: TEAL,
                                            lineHeight: 1, letterSpacing: '-2px',
                                        }}>
                                            {avgRating > 0 ? avgRating.toFixed(1) : '–'}
                                        </div>
                                        <div style={{ marginTop: '5px', marginBottom: '3px' }}>
                                            <StarRow filled={Math.round(avgRating)} size="0.72rem" />
                                        </div>
                                        <div style={{ fontSize: '0.62rem', color: '#94a3b8' }}>{reviewCount} ratings</div>
                                    </div>

                                    {/* Distribution bars */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {[5,4,3,2,1].map(s => (
                                            <DistBar key={s} star={s} count={dist[s]} total={reviews.length} />
                                        ))}
                                    </div>
                                </div>

                                {/* Verified badge */}
                                {summary?.verifiedCount > 0 && (
                                    <div style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                                        background: '#dcfce7', color: '#16a34a',
                                        border: '1px solid #bbf7d0', borderRadius: '20px',
                                        padding: '4px 12px', fontSize: '0.68rem', fontWeight: 700,
                                    }}>
                                        <i className="pi pi-verified" style={{ fontSize: '0.65rem' }} />
                                        {summary.verifiedCount} Verified {summary.verifiedCount === 1 ? 'Purchase' : 'Purchases'}
                                    </div>
                                )}
                            </div>

                            {/* ── AI insights ── */}
                            {(summary?.avgQuality || summary?.avgValue || summary?.avgDelivery) && (
                                <div style={{
                                    borderRadius: '14px',
                                    border: '1px solid #e0e7ff',
                                    background: 'rgba(11,49,99,0.04)',
                                    padding: '16px',
                                    marginBottom: '14px',
                                    boxShadow: '0 2px 12px rgba(99,102,241,0.08)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                                        <div style={{
                                            width: '26px', height: '26px', borderRadius: '7px',
                                            background: 'var(--primary-color)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            boxShadow: '0 3px 8px rgba(99,102,241,0.3)',
                                        }}>
                                            <i className="pi pi-sparkles" style={{ fontSize: '0.65rem', color: '#fff' }} />
                                        </div>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#4338ca' }}>
                                            Detailed Insights
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                                        <InsightBar label="Quality"  value={summary.avgQuality} />
                                        <InsightBar label="Value"    value={summary.avgValue} />
                                        <InsightBar label="Delivery" value={summary.avgDelivery} />
                                    </div>
                                </div>
                            )}

                            {/* ── Reviews section header ── */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                marginBottom: '12px',
                            }}>
                                <div style={{
                                    width: '4px', height: '18px',
                                    background: 'var(--primary-color)',
                                    borderRadius: '2px',
                                }} />
                                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e293b' }}>
                                    Customer Reviews
                                </span>
                                <span style={{
                                    marginLeft: 'auto',
                                    background: `${TEAL}18`, color: TEAL,
                                    border: `1px solid ${TEAL}30`, borderRadius: '999px',
                                    padding: '2px 10px', fontSize: '0.68rem', fontWeight: 700,
                                }}>
                                    {reviewCount}
                                </span>
                            </div>

                            {/* ── Review cards ── */}
                            <div>
                                {reviews.map((r, i) => <ReviewCard key={r._id || i} review={r} />)}
                            </div>

                            {/* ── Load more ── */}
                            {data?.pagination && page < data.pagination.totalPages && (
                                <button
                                    onClick={loadMore}
                                    style={{
                                        width: '100%', padding: '11px',
                                        background: '#fff',
                                        border: `1.5px solid ${TEAL_MID}`,
                                        borderRadius: '12px', cursor: 'pointer',
                                        fontSize: '0.78rem', fontWeight: 700, color: TEAL,
                                        display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: '6px',
                                        transition: 'box-shadow 0.2s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.boxShadow = `0 4px 16px ${TEAL}30`}
                                    onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                                >
                                    <i className="pi pi-chevron-down" /> Load more reviews
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Sidebar>
    );
};
